import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowLeft, 
  Send, 
  AlertCircle, 
  MessageCircle, 
  Loader2,
  Heart,
  Users,
  Briefcase,
  Home,
  Sparkles,
  Lightbulb,
  RefreshCw,
  Settings,
  Lock,
  ChevronDown,
  HelpCircle,
  X,
  Check,
  TrendingUp,
  BookOpen,
  Brain
} from "lucide-react";
import Logo from "@/components/Logo";
import SeverityMeter from "@/components/SeverityMeter";
import MessageRiskMeter from "@/components/MessageRiskMeter";
import AlternativeCard from "@/components/AlternativeCard";
import SuggestionCard from "@/components/SuggestionCard";
import ToneSelector from "@/components/ToneSelector";
import SampleMessages from "@/components/SampleMessages";
import ReviewerBadge from "@/components/ReviewerBadge";
import UpgradePrompt from "@/components/UpgradePrompt";
import PreLaunchUpgradePrompt from "@/components/PreLaunchUpgradePrompt";
import EarlyAccessBanner from "@/components/EarlyAccessBanner";
import ProPromoBanner from "@/components/ProPromoBanner";
import AdminPanel from "@/components/AdminPanel";
import EmailCapture from "@/components/EmailCapture";
import ConflictScoreCard from "@/components/ConflictScoreCard";
import ShareAppCard from "@/components/ShareAppCard";
import ShareResultCard from "@/components/ShareResultCard";
import {
  LockedRewriteCards,
  MoreIssuesChip,
  ImpactForecastCard,
  RedFlagPreviewCard,
  RecapsTeaserCard,
} from "@/components/ProTeasers";
import { useAccess } from "@/contexts/AccessContext";
import { useAuth } from "@/contexts/AuthContext";
import { Events } from "@/lib/analytics";
import { IS_PRELAUNCH } from "@/lib/pricing";
import { getTeaserVariant, trackExperimentEvent } from "@/lib/experiments";
import ReserveDealModal from "@/components/ReserveDealModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const relationshipOptions = [
  { id: "general", label: "General", icon: MessageCircle },
  { id: "romantic", label: "Romantic Partner", icon: Heart },
  { id: "family", label: "Family Member", icon: Home },
  { id: "friend", label: "Friend", icon: Users },
  { id: "work", label: "Work Colleague", icon: Briefcase }
];

// Rotating microcopy messages
const processingMessages = [
  "Analyzing tone and intent…",
  "Finding a calmer approach…",
  "Rewriting for clarity…",
  "Keeping your meaning, improving delivery…"
];

const successMessages = [
  "Ready when you are",
  "Take a look — does this feel right?",
  "You've got this",
  "Hope this helps"
];

const emptyStateMessages = [
  "Paste a message to see how SayItBetter can help.",
  "Need help wording something? Start here.",
  "Turn a rough draft into a better message."
];

// Helper to get random item from array
const getRandomMessage = (messages) => {
  return messages[Math.floor(Math.random() * messages.length)];
};

export default function AnalyzePage({ relationshipType, setRelationshipType }) {
  const navigate = useNavigate();
  const { authHeaders, isAuthenticated, user } = useAuth();
  const { 
    sessionId, 
    email, 
    accessStatus, 
    isReviewer, 
    hasFullAccess,
    isLimitedMode,
    remainingUses,
    freeTones,
    refreshAccess
  } = useAccess();
  
  const [text, setText] = useState("");
  const [receivedMessage, setReceivedMessage] = useState("");
  const [context, setContext] = useState("");
  const [selectedTone, setSelectedTone] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showReserve, setShowReserve] = useState(false);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [selectedType, setSelectedType] = useState(relationshipType?.id || "");
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  
  // Rotating microcopy state
  const [processingMessageIndex, setProcessingMessageIndex] = useState(0);
  const [currentProcessingMessage, setCurrentProcessingMessage] = useState(processingMessages[0]);
  const [emptyStateMessage] = useState(() => getRandomMessage(emptyStateMessages));
  const [successMessage, setSuccessMessage] = useState("");
  const [showEmailCapture, setShowEmailCapture] = useState(false);
  const [showHelpTip, setShowHelpTip] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return !localStorage.getItem("sib_help_tip_seen"); } catch { return false; }
  });

  // Check if we should show email capture (when limit reached, no email yet)
  const shouldShowEmailCapture = () => {
    const hasEmail = localStorage.getItem("sib_user_email");
    const emailCaptured = localStorage.getItem("sib_email_captured");
    const promptSeen = localStorage.getItem("sib_email_prompt_seen");
    
    // Don't show if user already has email or has seen/skipped the prompt
    return !hasEmail && !emailCaptured && !promptSeen;
  };

  // Rotate processing message every 2.5 seconds while analyzing
  useEffect(() => {
    if (!isAnalyzing && !isSuggesting) return;
    
    const interval = setInterval(() => {
      setProcessingMessageIndex((prev) => {
        const nextIndex = (prev + 1) % processingMessages.length;
        setCurrentProcessingMessage(processingMessages[nextIndex]);
        return nextIndex;
      });
    }, 2500);
    
    return () => clearInterval(interval);
  }, [isAnalyzing, isSuggesting]);

  // Set random success message when results arrive
  useEffect(() => {
    if (analysis || suggestions) {
      setSuccessMessage(getRandomMessage(successMessages));
    }
  }, [analysis, suggestions]);

  useEffect(() => {
    if (!relationshipType && !selectedType) {
      // Default to "general" when used inside the app shell (authenticated users)
      setSelectedType("general");
      setRelationshipType({ id: "general", label: "General", icon: MessageCircle });
    }
  }, [relationshipType, selectedType, setRelationshipType]);

  // Auto-dismiss the first-run help tip after 10s so it never feels nagging
  useEffect(() => {
    if (!showHelpTip) return;
    const t = setTimeout(() => dismissHelpTip(), 10000);
    return () => clearTimeout(t);
  }, [showHelpTip]);

  const dismissHelpTip = () => {
    setShowHelpTip(false);
    try { localStorage.setItem("sib_help_tip_seen", "1"); } catch {}
  };

  // Free-tier pro-teaser unlock trigger. Opens the Reserve modal in prelaunch,
  // navigates to the pricing page post-launch.
  const teaserVariant = getTeaserVariant(); // "full" | "minimal" — sticky per device
  const handleProTeaserUnlock = (teaserSource) => {
    Events.upgradeClick?.("yearly_5_99", `${teaserSource}:${teaserVariant}`);
    trackExperimentEvent("teaser_density_v1", "unlock_click");
    if (IS_PRELAUNCH) {
      setShowReserve(true);
    } else {
      navigate("/pricing");
    }
  };

  const handleSuggestResponse = async () => {
    if (!receivedMessage.trim()) {
      toast.error("Please paste the message you received first");
      return;
    }

    const currentType = selectedType || relationshipType?.id;
    if (!currentType) {
      toast.error("Please select a relationship type");
      return;
    }

    if (!selectedTone) {
      toast.error("Please select a tone for your reply");
      return;
    }

    // Check if user has remaining uses in limited mode
    if (isLimitedMode && !hasFullAccess && remainingUses !== null && remainingUses <= 0) {
      // Show email capture first if eligible, otherwise show upgrade prompt
      if (shouldShowEmailCapture()) {
        setShowEmailCapture(true);
      }
      setShowUpgradePrompt(true);
      return;
    }

    setIsSuggesting(true);
    setSuggestions(null);

    try {
      const response = await axios.post(`${API}/suggest`, {
        received_message: receivedMessage.trim(),
        relationship_type: currentType,
        tone: selectedTone,
        context: context.trim() || null,
        session_id: sessionId,
        email: email
      }, { headers: authHeaders() });
      setSuggestions(response.data);
      toast.success("Here are some thoughtful ways to respond");
      // Refresh access status to update remaining uses
      refreshAccess();
    } catch (error) {
      console.error("Suggest error:", error);
      if (error.response?.status === 429) {
        // Daily limit reached - show email capture if eligible
        if (shouldShowEmailCapture()) {
          setShowEmailCapture(true);
        }
        setShowUpgradePrompt(true);
      } else if (error.response?.status === 403) {
        toast.error("This tone is a premium feature. Select a free tone or upgrade.");
        setShowUpgradePrompt(true);
      } else {
        toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
      }
    } finally {
      setIsSuggesting(false);
    }
  };

  const handleAnalyze = async () => {
    if (!text.trim()) {
      toast.error("Please enter the message you'd like guidance on");
      return;
    }

    const currentType = selectedType || relationshipType?.id;
    if (!currentType) {
      toast.error("Please select a relationship type");
      return;
    }

    // Check if user has remaining uses in limited mode
    if (isLimitedMode && !hasFullAccess && remainingUses !== null && remainingUses <= 0) {
      // Show email capture first if eligible, otherwise show upgrade prompt
      if (shouldShowEmailCapture()) {
        setShowEmailCapture(true);
      }
      setShowUpgradePrompt(true);
      return;
    }

    setIsAnalyzing(true);
    setAnalysis(null);

    try {
      const response = await axios.post(`${API}/analyze`, {
        text: text.trim(),
        relationship_type: currentType,
        received_message: receivedMessage.trim() || null,
        context: context.trim() || null,
        session_id: sessionId,
        email: email
      }, { headers: authHeaders() });
      setAnalysis(response.data);
      Events.analyzeMessage(response.data.risk_level, currentType);
      toast.success("Here's what we noticed about your message");
      // Refresh access status to update remaining uses
      refreshAccess();
    } catch (error) {
      console.error("Analysis error:", error);
      if (error.response?.status === 429) {
        // Daily limit reached - show email capture if eligible
        if (shouldShowEmailCapture()) {
          setShowEmailCapture(true);
        }
        setShowUpgradePrompt(true);
      } else {
        toast.error(error.response?.data?.detail || "Something went wrong. Please try again.");
      }
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleBack = () => {
    setAnalysis(null);
    setSuggestions(null);
    setText("");
    setReceivedMessage("");
    // If we have a logged-in user, just reset the form (stay on home tab).
    // Otherwise, go back to landing.
    if (!isAuthenticated) {
      navigate("/");
    }
  };

  const handleRelationshipChange = (value) => {
    setSelectedType(value);
    const type = relationshipOptions.find(opt => opt.id === value);
    if (type) {
      setRelationshipType(type);
    }
  };

  const handleReset = () => {
    setText("");
    setReceivedMessage("");
    setContext("");
    setSelectedTone("");
    setAnalysis(null);
    setSuggestions(null);
  };

  return (
    <div className="min-h-screen pb-24 bg-[#F7F9FB]">
      {/* Pre-Launch Upgrade Prompt Modal (replaces old UpgradePrompt when limit reached) */}
      {showUpgradePrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="max-w-md w-full animate-fadeIn">
            <PreLaunchUpgradePrompt onClose={() => setShowUpgradePrompt(false)} />
          </div>
        </div>
      )}
      
      {/* Admin Panel Modal */}
      {showAdminPanel && (
        <AdminPanel onClose={() => setShowAdminPanel(false)} />
      )}

      {/* Email Capture Modal */}
      {showEmailCapture && (
        <EmailCapture 
          onClose={() => setShowEmailCapture(false)}
          onSubmit={(email) => {
            // Email saved via the component
          }}
        />
      )}

      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-[#E5E7EB] shadow-soft">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={handleBack}
            data-testid="back-button"
            className="gap-2 text-[#6B7280] hover:text-[#1F3A5F] hover:bg-[#EAF6F4] rounded-xl"
          >
            <ArrowLeft size={18} />
            <span className="hidden sm:inline">Back</span>
          </Button>
          
          <div className="flex items-center gap-3">
            <Logo size="small" />
            {isReviewer && <ReviewerBadge />}
          </div>
          
          <div className="flex items-center gap-1">
            <div className="relative">
              {showHelpTip && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 rounded-xl ring-2 ring-[#4FB3A5]/60 animate-ping"
                  data-testid="help-tip-pulse"
                />
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { dismissHelpTip(); navigate("/app/help"); }}
                data-testid="analyze-help-button"
                className="relative text-[#6B7280] hover:text-[#1F3A5F] hover:bg-[#EAF6F4] rounded-xl"
                aria-label="Help and FAQ"
              >
                <HelpCircle size={18} />
                {showHelpTip && (
                  <span
                    className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-[#4FB3A5] border-2 border-white"
                    aria-hidden="true"
                  />
                )}
              </Button>
              {showHelpTip && (
                <div
                  role="tooltip"
                  data-testid="help-tip-bubble"
                  className="absolute top-full right-0 mt-2 z-20 w-[230px] bg-[#1F3A5F] text-white text-[12px] leading-snug rounded-xl shadow-lg px-3 py-2.5 animate-in fade-in slide-in-from-top-1 duration-300"
                >
                  <div className="absolute -top-1.5 right-4 w-3 h-3 rotate-45 bg-[#1F3A5F]" aria-hidden="true" />
                  <div className="flex items-start gap-2">
                    <span className="flex-1">
                      New here? Tap the <span className="font-semibold">?</span> anytime for tips and FAQs.
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); dismissHelpTip(); }}
                      data-testid="help-tip-dismiss"
                      className="text-white/70 hover:text-white -mr-1 -mt-0.5"
                      aria-label="Dismiss tip"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAdminPanel(true)}
              data-testid="admin-button"
              className="text-[#6B7280] hover:text-[#1F3A5F] hover:bg-[#EAF6F4] rounded-xl"
            >
              <Settings size={18} />
            </Button>
          </div>
        </div>
        
        {/* Usage indicator for limited mode - Pre-Launch */}
        {isLimitedMode && !hasFullAccess && remainingUses !== null && (
          <div className="max-w-4xl mx-auto px-4 pb-2">
            <div className={`text-xs text-center py-2 px-4 rounded-xl inline-flex items-center gap-2
              ${remainingUses > 0 
                ? 'bg-gradient-to-r from-[#EAF6F4] to-[#F0F9F7] text-[#4FB3A5] border border-[#4FB3A5]/20' 
                : 'bg-gradient-to-r from-amber-50 to-orange-50 text-amber-600 border border-amber-200'}
            `} data-testid="usage-indicator">
              <span className={`w-2 h-2 rounded-full ${remainingUses > 0 ? 'bg-[#4FB3A5]' : 'bg-amber-400 animate-pulse'}`}></span>
              {remainingUses > 0 
                ? `${remainingUses} of 3 free message checks remaining today`
                : 'Daily limit reached — Upgrade to PRO for unlimited'
              }
            </div>
          </div>
        )}
      </div>

      <div className="max-w-4xl mx-auto px-4 pt-4">
        {/* Pro Promo Banner - shown only for authenticated users (inside app shell) */}
        {isAuthenticated && <div className="-mx-4 mb-4"><ProPromoBanner /></div>}

        {/* Conflict Score Card — shown once user has at least 1 analysis */}
        {isAuthenticated && !analysis && !suggestions && (
          <div className="mb-4">
            <ConflictScoreCard
              isPro={user?.pro_status === "active"}
              onUpgradeClick={() => {
                Events.upgradeClick("yearly_5_99", "conflict_score_card");
                if (IS_PRELAUNCH) {
                  setShowReserve(true);
                } else {
                  navigate("/pricing");
                }
              }}
            />
          </div>
        )}

        {/* Hero / Value section — shown on clean Home state (before any analysis rendered) */}
        {!analysis && !suggestions && (
          <div
            className="relative overflow-hidden rounded-3xl mb-5 p-6 text-white shadow-xl"
            style={{
              background:
                "linear-gradient(135deg, #0B1A33 0%, #1F3A5F 45%, #2F5BFF 100%)",
            }}
            data-testid="value-hero"
          >
            <span
              className="absolute -top-12 -right-10 w-48 h-48 rounded-full opacity-30 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, #FFD27F 0%, transparent 65%)",
              }}
            />
            <span
              className="absolute -bottom-16 -left-12 w-56 h-56 rounded-full opacity-25 pointer-events-none"
              style={{
                background:
                  "radial-gradient(circle, #4FB3A5 0%, transparent 65%)",
              }}
            />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/25 text-white text-[10px] font-bold px-3 py-1 rounded-full mb-3">
                <Sparkles size={11} />
                AI TEXT COACH
              </div>
              <h1 className="font-serif text-[26px] sm:text-[30px] font-bold leading-[1.15] tracking-tight mb-2">
                Know how your message will land before you send it.
              </h1>
              <p className="text-[13px] text-white/85 leading-relaxed mb-4">
                SayItBetter helps you avoid misunderstandings, reduce conflict,
                and sound more clear, calm, and emotionally intelligent in your
                texts.
              </p>
              <ul className="space-y-2" data-testid="value-bullets">
                {[
                  "Check if your text sounds harsh, cold, needy, or confusing",
                  "Get a better version before you hit send",
                  "Build stronger communication habits over time",
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[13px] text-white/95 leading-snug">
                    <span className="w-5 h-5 rounded-full bg-[#4FB3A5] flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                      <Check size={11} className="text-white" strokeWidth={3} />
                    </span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Texting Wrapped sneak peek chip */}
        {!analysis && !suggestions && (
          <button
            onClick={() => navigate("/wrapped-preview")}
            data-testid="wrapped-teaser-chip"
            className="w-full mb-4 group relative overflow-hidden rounded-2xl border border-[#4FB3A5]/30 bg-white p-3.5 text-left shadow-soft active:scale-[0.99] transition-transform hover:border-[#4FB3A5]/60"
          >
            <div className="relative flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1F3A5F] to-[#4FB3A5] flex items-center justify-center flex-shrink-0">
                <Sparkles size={16} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-widest font-bold text-[#4FB3A5]">
                    New · Launch exclusive
                  </span>
                </div>
                <div className="text-[13px] font-bold text-[#1F3A5F] leading-tight truncate">
                  See a Texting Wrapped™ demo →
                </div>
                <div className="text-[11px] text-[#6B7280] leading-snug">
                  Your shareable year-in-review card. Reserve to unlock yours.
                </div>
              </div>
            </div>
          </button>
        )}

        {/* Input Section */}
        <div className="bg-white rounded-2xl shadow-soft p-6 mb-8 animate-fadeIn" data-testid="input-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#EAF6F4] flex items-center justify-center">
              <MessageCircle className="text-[#4FB3A5]" size={20} />
            </div>
            <div>
              <h2 className="font-semibold text-lg text-[#1F3A5F]" data-testid="demo-intro-title">
                Try the demo in seconds
              </h2>
              <p className="text-sm text-[#6B7280] leading-snug">
                Paste a message you're thinking about sending and see how
                SayItBetter scores the tone, flags conflict risk, and suggests a
                better version.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            {/* Relationship Selector */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-[#6B7280] whitespace-nowrap font-medium">
                I'm messaging my:
              </span>
              <Select 
                value={selectedType || relationshipType?.id || ""} 
                onValueChange={handleRelationshipChange}
                data-testid="relationship-select"
              >
                <SelectTrigger 
                  className="w-full max-w-[200px] bg-white border-[#E5E7EB] focus:border-[#4FB3A5] focus:ring-[#4FB3A5]/20 rounded-xl" 
                  data-testid="relationship-trigger"
                >
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {relationshipOptions.map((opt) => {
                    const Icon = opt.icon;
                    return (
                      <SelectItem 
                        key={opt.id} 
                        value={opt.id}
                        data-testid={`option-${opt.id}`}
                        className="rounded-lg"
                      >
                        <div className="flex items-center gap-2">
                          <Icon size={16} className="text-[#4FB3A5]" />
                          {opt.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Received Message Input - Premium feature in LIMITED mode */}
            {hasFullAccess ? (
              <div className="space-y-2">
                <label className="text-sm font-medium text-[#2C2C2C]">
                  What did they say to you? <span className="text-[#A0A7B4] font-normal">(optional)</span>
                </label>
                <Textarea
                  placeholder="Paste their message here — it helps us understand the context better..."
                  value={receivedMessage}
                  onChange={(e) => setReceivedMessage(e.target.value)}
                  className="min-h-[100px] resize-none bg-[#F7F9FB] border-[#E5E7EB] rounded-xl text-base
                             focus:border-[#4FB3A5] focus:ring-[#4FB3A5]/20 placeholder:text-[#A0A7B4]"
                  data-testid="received-message-input"
                />
              </div>
            ) : (
              <div 
                onClick={() => setShowUpgradePrompt(true)}
                className="flex items-center gap-2 p-4 bg-gray-50 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors"
                data-testid="received-message-locked"
              >
                <Lock size={16} className="text-gray-400" />
                <span className="text-sm text-gray-400">What did they say to you?</span>
                <span className="text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">Premium</span>
              </div>
            )}

            {/* Tone Selector - Shows when received message is entered (only for premium users) */}
            {hasFullAccess && receivedMessage.trim() && (
              <div className="bg-[#F7F9FC] rounded-2xl p-5 border border-[#E5E7EB]">
                <ToneSelector 
                  selectedTone={selectedTone}
                  onSelectTone={setSelectedTone}
                  disabled={isSuggesting}
                  freeTones={hasFullAccess ? null : freeTones}
                  onLockedClick={() => setShowUpgradePrompt(true)}
                />
              </div>
            )}

            {/* Suggest Response Button - Only for premium users */}
            {hasFullAccess && receivedMessage.trim() && (
              <Button
                variant="outline"
                onClick={handleSuggestResponse}
                disabled={isSuggesting || !selectedTone}
                className={`w-full py-5 text-sm font-medium rounded-xl border-2 transition-all duration-200
                  ${selectedTone 
                    ? 'border-[#2F5BFF] text-[#2F5BFF] hover:bg-[#2F5BFF]/5' 
                    : 'border-[#E5E7EB] text-[#A0A7B4] cursor-not-allowed'
                  }`}
                data-testid="suggest-response-button"
              >
                {isSuggesting ? (
                  <>
                    <Loader2 className="mr-2 animate-spin text-[#2F5BFF]" size={18} />
                    Finding a calmer approach...
                  </>
                ) : (
                  <>
                    <Lightbulb className="mr-2" size={18} />
                    {selectedTone ? 'Generate Response' : 'Select a tone above'}
                  </>
                )}
              </Button>
            )}

            {/* Your Reply Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-[#2C2C2C]">
                What do you want to say?
              </label>
              <Textarea
                placeholder="Paste the message you want help rewriting…"
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="min-h-[120px] resize-none bg-white border-[#E5E7EB] rounded-xl text-base
                           focus:border-[#4FB3A5] focus:ring-[#4FB3A5]/20 placeholder:text-[#A0A7B4]"
                data-testid="message-input"
              />
              
              {/* Empty state hint - rotating message */}
              {!text.trim() && !analysis && (
                <p className="text-xs text-[#A0A7B4] mt-1">
                  {emptyStateMessage}
                </p>
              )}
              
              {/* Sample Messages - Show when text is empty */}
              {!text.trim() && !analysis && (
                <div className="mt-4 p-4 bg-[#F7F9FC] rounded-xl border border-[#E5E7EB]">
                  <SampleMessages 
                    onSelectSample={(message) => {
                      setText(message);
                      toast.success("Example loaded — feel free to edit it");
                    }} 
                  />
                </div>
              )}
            </div>

            {/* Context Input - Premium feature in LIMITED mode */}
            {hasFullAccess ? (
              <details className="group">
                <summary className="text-sm text-[#6B7280] cursor-pointer hover:text-[#4FB3A5] transition-colors list-none flex items-center gap-2 font-medium">
                  <span className="group-open:hidden">+ Add more context (optional)</span>
                  <span className="hidden group-open:inline">− Hide context</span>
                </summary>
                <Textarea
                  placeholder="What's the situation? Any background that might help us understand better?"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  className="mt-3 min-h-[80px] resize-none bg-[#F7F9FB] border-[#E5E7EB] rounded-xl text-sm
                             focus:border-[#4FB3A5] focus:ring-[#4FB3A5]/20 placeholder:text-[#A0A7B4]"
                  data-testid="context-input"
                />
              </details>
            ) : (
              <div 
                onClick={() => setShowUpgradePrompt(true)}
                className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer hover:text-gray-500 transition-colors"
                data-testid="context-locked"
              >
                <Lock size={14} />
                <span>+ Add more context</span>
                <span className="text-xs bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">Premium</span>
              </div>
            )}

            {/* Analyze Button */}
            <Button
              onClick={handleAnalyze}
              disabled={isAnalyzing || !text.trim()}
              className="w-full py-6 text-base font-semibold rounded-xl bg-[#1F3A5F] hover:bg-[#2a4a75] 
                         text-white shadow-soft transition-all duration-200 disabled:bg-[#A0A7B4]"
              data-testid="analyze-button"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={20} />
                  <span className="transition-opacity duration-300">{currentProcessingMessage}</span>
                </>
              ) : (
                <>
                  <Sparkles className="mr-2" size={20} />
                  Rewrite Message
                </>
              )}
            </Button>
            
            {/* Scroll indicator when results are ready */}
            {(analysis || suggestions) && (
              <div 
                className="flex flex-col items-center gap-2 pt-4 animate-bounce cursor-pointer"
                onClick={() => {
                  document.querySelector('[data-testid="severity-card"], [data-testid="suggestions-card"]')?.scrollIntoView({ behavior: 'smooth' });
                }}
                data-testid="scroll-indicator"
              >
                <span className="text-sm text-[#4FB3A5] font-medium">Ready when you are — scroll down</span>
                <ChevronDown className="text-[#4FB3A5]" size={24} />
              </div>
            )}
          </div>
        </div>

        {/* Suggested Responses */}
        {suggestions && (
          <div className="bg-white rounded-2xl shadow-soft p-6 mb-8 animate-fadeIn" data-testid="suggestions-card">
            {/* Early Access Banner for Free Users */}
            {isLimitedMode && !hasFullAccess && (
              <div className="mb-4">
                <EarlyAccessBanner />
              </div>
            )}
            
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-[#EAF6F4] flex items-center justify-center">
                <Lightbulb className="text-[#4FB3A5]" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-[#1F3A5F]">
                  Suggested rewrites
                </h3>
                <p className="text-sm text-[#6B7280]">{successMessage}</p>
              </div>
            </div>
            <p className="text-sm text-[#6B7280] mb-6 leading-relaxed bg-[#F7F9FB] p-4 rounded-xl">
              {suggestions.guidance}
            </p>
            <div className="space-y-4">
              {suggestions.suggestions.map((sug, idx) => (
                <SuggestionCard 
                  key={idx} 
                  suggestion={sug} 
                  index={idx}
                  onUse={(text) => setText(text)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Analysis Results */}
        {analysis && (
          <div className="space-y-6 animate-fadeIn">
            {/* Early Access Banner for Free Users */}
            {isLimitedMode && !hasFullAccess && (
              <EarlyAccessBanner />
            )}
            
            {/* Message Risk Meter - New Traffic Light UI */}
            <div className="bg-white rounded-2xl shadow-soft p-6" data-testid="risk-meter-card">
              <h3 className="font-semibold text-lg text-[#1F3A5F] mb-4">
                Message Risk Assessment
              </h3>
              <MessageRiskMeter 
                riskScore={analysis.risk_score}
                riskLevel={analysis.risk_level}
                shortReason={analysis.short_reason}
                quickTip={analysis.quick_tip}
              />
            </div>

            {/* Severity Meter */}
            <div className="bg-white rounded-2xl shadow-soft p-6" data-testid="severity-card">
              <h3 className="font-semibold text-lg text-[#1F3A5F] mb-4">
                How this message might land
              </h3>
              <SeverityMeter score={analysis.severity_score} />
            </div>

            {/* Pro teasers — free tier only. Density is A/B-tested: "full" = all 4 teasers, "minimal" = Impact Forecast only. */}
            {!hasFullAccess && (
              <>
                <ImpactForecastCard
                  riskLevel={analysis.risk_level}
                  onUnlock={() => handleProTeaserUnlock("impact_forecast_card")}
                />
                {teaserVariant === "full" && (
                  <RedFlagPreviewCard
                    originalText={analysis.original_text || text}
                    onUnlock={() => handleProTeaserUnlock("red_flag_card")}
                  />
                )}
                <RecapsTeaserCard
                  onOpenPreview={() => {
                    Events.trackEvent?.("recaps_teaser_preview_open", { source: "post_analysis" });
                    navigate("/wrapped-preview");
                  }}
                  onUnlock={() => handleProTeaserUnlock("recaps_teaser_card")}
                />
              </>
            )}

            {/* Analysis */}
            <div className="bg-white rounded-2xl shadow-soft p-6" data-testid="analysis-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#FEF3F2] flex items-center justify-center">
                  <AlertCircle className="text-[#E07A7A]" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-[#1F3A5F]">
                    Something to consider
                  </h3>
                  <p className="text-sm text-[#6B7280]">Not a criticism — just perspective</p>
                </div>
              </div>
              
              <p className="text-[#2C2C2C] leading-relaxed mb-6" data-testid="analysis-text">
                {analysis.analysis}
              </p>

              {analysis.communication_issues.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-[#6B7280] mb-3">
                    Patterns we noticed:
                  </h4>
                  <div className="flex flex-wrap gap-2 items-center" data-testid="issues-list">
                    {(hasFullAccess || teaserVariant === "minimal"
                      ? analysis.communication_issues
                      : analysis.communication_issues.slice(0, 2)
                    ).map((issue, idx) => (
                      <Badge
                        key={idx}
                        className="bg-[#FEF3F2] text-[#E07A7A] border-0 text-sm py-1.5 px-3 font-medium rounded-lg"
                      >
                        {issue}
                      </Badge>
                    ))}
                    {!hasFullAccess && teaserVariant === "full" && analysis.communication_issues.length > 2 && (
                      <MoreIssuesChip
                        hiddenCount={analysis.communication_issues.length - 2}
                        onUnlock={() => handleProTeaserUnlock("issues_more_chip")}
                      />
                    )}
                  </div>
                </div>
              )}

              {analysis.potential_harm.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-[#6B7280] mb-3">
                    How this might affect your relationship:
                  </h4>
                  <ul className="space-y-2" data-testid="harm-list">
                    {analysis.potential_harm.map((harm, idx) => (
                      <li key={idx} className="text-sm text-[#2C2C2C] flex items-start gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#E07A7A] mt-2 flex-shrink-0"></span>
                        {harm}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Alternative Suggestions */}
            <div className="bg-white rounded-2xl shadow-soft p-6" data-testid="alternatives-card">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-[#ECFDF5] flex items-center justify-center">
                  <Send className="text-[#7BC4A4]" size={20} />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-[#1F3A5F]">
                    Suggested rewrites
                  </h3>
                  <p className="text-sm text-[#6B7280]">Keeping your meaning, improving delivery</p>
                </div>
              </div>
              <p className="text-sm text-[#6B7280] mb-6">
                Click any suggestion to copy it
              </p>
              <div className="space-y-4">
                {(hasFullAccess || teaserVariant === "minimal"
                  ? analysis.alternatives
                  : analysis.alternatives.slice(0, 1)
                ).map((alt, idx) => (
                  <AlternativeCard key={idx} alternative={alt} index={idx} />
                ))}
                {!hasFullAccess && teaserVariant === "full" && analysis.alternatives.length > 1 && (
                  <LockedRewriteCards
                    count={Math.min(analysis.alternatives.length - 1, 2)}
                    onUnlock={() => handleProTeaserUnlock("locked_rewrite_card")}
                  />
                )}
              </div>
              
              {/* Success message - random from rotation */}
              <p className="text-center text-sm text-[#4FB3A5] mt-6 font-medium">
                {successMessage} 💪
              </p>
            </div>

            {/* Share your result — generates a branded image users can post to social */}
            <ShareResultCard analysis={analysis} />

            {/* Unlock Communication Coaching — paid value teaser */}
            <div
              className="rounded-3xl p-6 text-white shadow-xl relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(135deg, #1F3A5F 0%, #2a4a75 45%, #4FB3A5 100%)",
              }}
              data-testid="coaching-teaser-card"
            >
              <span
                className="absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-30 pointer-events-none"
                style={{
                  background:
                    "radial-gradient(circle, #FFD27F 0%, transparent 65%)",
                }}
              />
              <div className="relative">
                <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur-sm border border-white/25 text-white text-[10px] font-bold px-3 py-1 rounded-full mb-3">
                  <Brain size={11} />
                  COACHING UNLOCKS NEXT
                </div>
                <h3 className="text-xl font-black leading-tight mb-1.5">
                  Unlock Communication Coaching
                </h3>
                <p className="text-[13px] text-white/85 leading-relaxed mb-4">
                  Pro learns your communication style over time, saves message
                  history, and gives you personalized feedback, coaching tips,
                  and improvement suggestions.
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-5" data-testid="coaching-bullets">
                  {[
                    { icon: BookOpen, text: "Saved message history" },
                    { icon: Brain,    text: "Personalized communication style insights" },
                    { icon: Lightbulb,text: "Weekly coaching feedback" },
                    { icon: TrendingUp,text: "Progress reports that show how you're improving" },
                  ].map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-[12px] leading-snug">
                      <span className="w-7 h-7 rounded-lg bg-white/15 backdrop-blur-sm border border-white/25 flex items-center justify-center flex-shrink-0">
                        <f.icon size={13} className="text-white" />
                      </span>
                      <span className="text-white/95 mt-1">{f.text}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-2xl px-4 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#FFD27F] mb-0.5">
                      Coming soon
                    </div>
                    <div className="text-[14px] font-bold leading-tight">
                      Pro is coming for just <span className="text-white">$2.99/month</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleProTeaserUnlock("coaching_teaser_card")}
                    data-testid="coaching-teaser-cta"
                    className="bg-white text-[#1F3A5F] font-black text-[12px] px-4 py-2.5 rounded-xl active:scale-95 transition-all shadow-md whitespace-nowrap"
                  >
                    Reserve early access
                  </button>
                </div>
              </div>
            </div>

            {/* Communication Progress Reports — separate value teaser */}
            <div
              className="rounded-3xl p-5 bg-white shadow-soft border border-[#E5E7EB]"
              data-testid="progress-reports-card"
            >
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#4FB3A5] to-[#2F5BFF] flex items-center justify-center flex-shrink-0 shadow-sm">
                  <TrendingUp size={18} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[16px] font-bold text-[#1F3A5F] leading-tight mb-1">
                    Communication Progress Reports
                  </h3>
                  <p className="text-[12px] text-[#6B7280] leading-snug mb-3">
                    See how your tone and communication habits improve over
                    time with personalized feedback and coaching.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate("/wrapped-preview")}
                    data-testid="progress-reports-preview-btn"
                    className="inline-flex items-center gap-1 text-[12px] font-semibold text-[#2F5BFF] hover:underline"
                  >
                    See a sample report →
                  </button>
                </div>
              </div>
            </div>

            {/* Share App prompt — shown post-analysis (aha moment for virality) */}
            <ShareAppCard source="post_analysis" />

            {/* Reset Button */}
            <div className="flex justify-center pt-4">
              <Button 
                variant="outline" 
                onClick={handleReset}
                data-testid="reset-button"
                className="gap-2 rounded-xl border-2 border-[#4FB3A5] text-[#1F3A5F] hover:bg-[#EAF6F4]"
              >
                <RefreshCw size={18} className="text-[#4FB3A5]" />
                Start a new message
              </Button>
            </div>
          </div>
        )}
      </div>

      <ReserveDealModal
        isOpen={showReserve}
        onClose={() => setShowReserve(false)}
        source="conflict_score_card"
        defaultEmail={user?.email || ""}
      />
    </div>
  );
}
